import { Suspense } from 'react';

import { Group, Stack } from '@mantine/core';

import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';

import { NAVBAR_HEIGHT } from '../../shared/components/UI/Navbar/Navbar';

import { Breadcrumbs } from '../../features/bidResponse/components/responseProject/components/responseProjectDetail/components/breadcrumbs/Breadcrumbs';
import { ResponseProjectAssistantProvider } from '../../features/bidResponse/components/responseProject/components/responseProjectDetail/components/responseProjectAssistant/context/responseProjectAssistant.provider';
import { ResponseProjectDetailAside } from '../../features/bidResponse/components/responseProject/components/responseProjectDetail/components/responseProjectDetailAside/ResponseProjectDetailAside';
import { ResponseProjectDetailAsideLoading } from '../../features/bidResponse/components/responseProject/components/responseProjectDetail/components/responseProjectDetailAside/ResponseProjectDetailAsideLoading';
import { ResponseProjectDetailBlocks } from '../../features/bidResponse/components/responseProject/components/responseProjectDetail/components/responseProjectDetailBlocks/ResponseProjectDetailBlocks';
import { ResponseProjectDetailBlocksLoading } from '../../features/bidResponse/components/responseProject/components/responseProjectDetail/components/responseProjectDetailBlocks/ResponseProjectDetailBlocksLoading';
import { ResponseProjectDetailProvider } from '../../features/bidResponse/components/responseProject/components/responseProjectDetail/context/responseProjectDetailContext';
import { useGetOneResponseProject } from '../../features/bidResponse/components/responseProject/hooks/useGetOneResponseProject.hooks';
import { withFeatureFlag } from '../../shared/components/FeatureFlagGuard';
import { FeatureFlag } from '../../shared/entities/FeatureFlags';
import { capitalizeFirstLetter } from '../../shared/utils/strings';

export function ResponseProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return <Navigate to="/response/projects" />;
  }

  return withFeatureFlag(
    <Suspense fallback={<Loading />}>
      <SafeResponseProjectDetailPage id={Number(id)} />
    </Suspense>,
    FeatureFlag.PRE_RELEASE,
  );
}

function Loading() {
  return (
    <Wrapper
      breadcrumbs={
        <Breadcrumbs.Loading>
          <Breadcrumbs.ItemLoading />
          <Breadcrumbs.ItemLoading />
        </Breadcrumbs.Loading>
      }
      aside={<ResponseProjectDetailAsideLoading />}
      main={<ResponseProjectDetailBlocksLoading />}
    />
  );
}

function SafeResponseProjectDetailPage({ id }: { id: number }) {
  const { t } = useTranslation('bidResponse');
  const { data: project } = useGetOneResponseProject(id);

  return (
    <ResponseProjectDetailProvider projectId={id}>
      <ResponseProjectAssistantProvider>
        <Wrapper
          breadcrumbs={
            <Breadcrumbs>
              <Breadcrumbs.Item
                label={t('projectDetail.breadcrumbs.allProjects')}
                path="/response/projects"
              />
              <Breadcrumbs.Item
                label={capitalizeFirstLetter(project.title.toLowerCase())}
                path={`/response/projects/${id}`}
              />
            </Breadcrumbs>
          }
          aside={<ResponseProjectDetailAside projectId={id} />}
          main={<ResponseProjectDetailBlocks projectId={id} />}
        />
      </ResponseProjectAssistantProvider>
    </ResponseProjectDetailProvider>
  );
}

interface WrapperProps {
  aside: React.ReactNode;
  main: React.ReactNode;
  breadcrumbs: React.ReactNode;
}

function Wrapper({ aside, main, breadcrumbs }: WrapperProps) {
  return (
    <Group
      w="100vw"
      p="05"
      spacing="05"
      align="start"
      h={`calc(100vh - ${NAVBAR_HEIGHT}px)`}
      sx={{
        overflow: 'hidden',
      }}
    >
      <Stack w={'100vw'}>{breadcrumbs}</Stack>
      <Stack w={332}>{aside}</Stack>
      <Stack sx={{ flex: 1 }} h="100%">
        {main}
      </Stack>
    </Group>
  );
}
